import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import MeshList from 'components/MeshList';
import { LevelUpContextProvider } from 'components/MeshList/levelUp/context/provider';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { OneOnOneAnalyticsProvider } from 'screens/analytics/1on1/OneOnOneAnalyticsContext';
import { HeaderSkeleton, MeshlistSkeleton, OneOnOneTileSkeleton } from 'screens/oneOnOne/commonComponents';
import OneOnOneSidePanelContextProvider from 'screens/oneOnOne/context/SidePanelContext';
import SubmissionMessage from 'screens/oneOnOne/instancePage/SubmissionMessage';
import SubmissionRating from 'screens/oneOnOne/instancePage/SubmissionRating';
import Questions from 'screens/oneOnOne/instancePage/questionsPage';
import { AgendaSidePanelContextProvider } from 'screens/oneOnOne/levelUp/agendaSidePanel/context/provider';
import { LevelUpSidePanelContextProvider } from 'screens/oneOnOne/levelUp/competencySidePanel/context/provider';
import LevelUpMeshListHeader from 'screens/oneOnOne/levelUp/levelUpMeshList/components/LevelUpMeshListHeader';
import { ReviewProvider } from 'screens/performanceReview/ReviewContext';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { HideScrollbarCSS } from 'styles/common';

import { ONE_ON_ONE_INSTANCE_VIEWS, ONE_ON_ONE_STATUSES, PAGE_TYPES } from 'constants/ProjectConstants';
import { sendClickedOnRescheduleEvent } from 'constants/mixpanelHelper';

import { getCheckInTimeline } from 'services/OneOnOne';
import { showErrorSnackbar } from 'services/snackbar';
import { getTaskDetails, updateCheckInDateTime } from 'services/task';

const Container = styled.div`
  padding-top: 1rem;
  max-width: 1200px;
  width: 80%;
  height: 100%;
  overflow-y: hidden;

  margin: 0 auto;
  ${(props) => props.theme.breakpoints.up('md')} {
    width: 75%;
  }
  ${(props) => props.theme.breakpoints.up('xl')} {
    max-width: 1440px;
    width: 70%;
  }

  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: 2rem;
`;

const Sidebar = styled.aside`
  overflow: hidden;
  height: 100%;
  overflow-y: scroll;
  ${HideScrollbarCSS};
`;

const ContentContainer = styled.div`
  overflow-y: scroll;
  ${HideScrollbarCSS};
  position: relative;
`;

const getOtherPerson = (employees, employeeId) => employees.filter((employee) => employee.id !== employeeId);

const defaultContext = {
  instanceDetails: null,
  otherMember: null,
  loggedInEmployee: null,
  comments: [],
  isInstanceEditable: true,
};

export const OneOnOneInstanceContext = createContext(defaultContext);

export const OneOnOneInstanceContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [instanceDetails, setInstanceDetails] = useState(null);
  const [currentTimeline, setcurrentTimeline] = useState(null);
  const [comments, setComments] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [mappedTimelineData, setMappedTimelineData] = useState([]);

  const [endMeetingModal, setEndMeetingModal] = useState(false);
  const [cancelMeetingModal, setcancelMeetingModal] = useState(false);

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const reportee = useMemo(() => {
    if (!instanceDetails) return null;
    return getOtherPerson(instanceDetails.toIds, instanceDetails.from.id)[0];
  }, [instanceDetails, loggedInEmployee]);

  const otherEmployee = useMemo(() => {
    if (!instanceDetails) return null;

    return getOtherPerson(instanceDetails.toIds, loggedInEmployee.id)[0];
  }, [instanceDetails, loggedInEmployee]);

  const updateOneOnOne = (updatedOneOnOne) => {
    setInstanceDetails((prev) => ({ ...prev, ...updatedOneOnOne }));
  };

  const updateInstanceDetails = (details) => {
    setInstanceDetails(details);

    getCheckInTimeline({ checkInId: details.parentTaskId, offset: 0, limit: 100 })
      .then((res) => {
        const tLData = res.filter((dataObj) => dataObj.status !== ONE_ON_ONE_STATUSES.DISABLED);
        const mappedTLData = tLData.map((dataObj) => ({
          instanceId: dataObj.id,
          dueTS: dataObj.dueTS,
          createdTS: dataObj.createdTS,
        }));
        const currentTimeline = res.find((timeline) => timeline.id?.toString() === details.id);
        setcurrentTimeline(currentTimeline);
        setTimelineData(tLData);
        setMappedTimelineData(mappedTLData);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const toggleEndMeetingModal = useCallback(() => {
    setEndMeetingModal((prevVal) => !prevVal);
  }, []);

  const toggleCancelMeetingModal = useCallback(() => {
    setcancelMeetingModal((prevVal) => !prevVal);
  }, []);

  const updateDetailedTimeline = (checkInId, checkInState) => {
    const updatedTimelineData = timelineData.map((timeline) =>
      timeline.id === parseInt(checkInId) ? { ...timeline, status: checkInState } : timeline
    );

    setTimelineData(updatedTimelineData);
  };

  const dateTimeChangeHandler = async (newStartDateTime, newDueDateTime) => {
    if (!newStartDateTime || !newDueDateTime) {
      dispatch(showErrorSnackbar('Please select a date for 1:1'));
      return;
    }

    try {
      const res = await updateCheckInDateTime({
        checkInInstanceId: instanceDetails.id,
        dueDate: newDueDateTime.getTime(),
        startDate: newStartDateTime.getTime(),
      });

      updateOneOnOne(res);
      sendClickedOnRescheduleEvent({
        eventProperties: {
          role: loggedInEmployee.userRole,
        },
      });
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || 'Request Failed: Please check your connection'));
      console.error(err);
    }
  };

  return (
    <OneOnOneInstanceContext.Provider
      value={{
        state: {
          instanceDetails,
          otherEmployee,
          loggedInEmployee,
          comments,
          timelineData: mappedTimelineData,
          detailedTimeLineData: timelineData,
          reportee,
          endMeetingModal,
          cancelMeetingModal,
          currentTimeline,
        },
        updateInstanceDetails,
        updateOneOnOne,
        setComments,
        toggleCancelMeetingModal,
        toggleEndMeetingModal,
        dateTimeChangeHandler,
        updateDetailedTimeline,
      }}
    >
      {children}
    </OneOnOneInstanceContext.Provider>
  );
};

const InstancePage = ({ employee, setReportee, setCheckInInstanceId, setCheckInParentId, setFilterEmpIds }) => {
  const { instanceId } = useParams();

  const {
    state: { instanceDetails, loggedInEmployee, otherEmployee, reportee },
    updateInstanceDetails,
  } = useContext(OneOnOneInstanceContext);

  const checkInInstanceId = instanceDetails?.id;
  const checkInParentId = instanceDetails?.parentTaskId;

  const filterEmpIds = Array.isArray(instanceDetails?.toIds) ? instanceDetails.toIds.map((empItem) => empItem.id) : [];

  useEffect(() => {
    setReportee(reportee);
    setCheckInInstanceId(checkInInstanceId);
    setCheckInParentId(checkInParentId);
    setFilterEmpIds(filterEmpIds);
  }, [reportee]);

  const getInstanceDetails = () =>
    getTaskDetails({ taskId: instanceId })
      .then((res) => {
        updateInstanceDetails(res);
      })
      .catch((err) => {
        console.log(err);
      });

  const reloadPage = () => getInstanceDetails();

  useEffect(() => {
    getInstanceDetails();
  }, [instanceId]);

  return (
    <Container>
      <ContentContainer>
        {instanceDetails ? (
          <InstanceView
            reloadPage={reloadPage}
            instanceDetails={instanceDetails}
            loggedInEmployee={loggedInEmployee}
            otherEmployee={otherEmployee}
            reportee={reportee}
          />
        ) : (
          <>
            <HeaderSkeleton />
            <OneOnOneTileSkeleton />
          </>
        )}
      </ContentContainer>
      {reportee ? (
        <Sidebar>
          <ReviewProvider>
            <MeshList
              levelUpProps={{
                checkInInstanceId,
                checkInParentId,
                filterEmpIds,
              }}
              employee={reportee}
              pageType={PAGE_TYPES.oneOnOne}
              headerText={`${reportee.firstName}'s Progress`}
              forceLoggedInEmployeeAsFalse
              showLevelUpTab={instanceDetails?.showLevelUpPanel}
              headerComponent={() => <LevelUpMeshListHeader reportee={reportee} />}
            />
          </ReviewProvider>
        </Sidebar>
      ) : (
        <MeshlistSkeleton />
      )}
    </Container>
  );
};

export const InstanceView = ({ instanceDetails, loggedInEmployee, otherEmployee, reloadPage, reportee }) => {
  const [currentView, setCurrentView] = useState(ONE_ON_ONE_INSTANCE_VIEWS.INSTANCE_DETAILS);
  const history = useHistory();

  const isManager = instanceDetails?.from?.id === loggedInEmployee?.id;

  const onViewInstanceDetails = () => {
    reloadPage().then((res) => {
      setCurrentView(ONE_ON_ONE_INSTANCE_VIEWS.INSTANCE_DETAILS);
    });
  };

  const goBack = () => {
    history.goBack();
  };

  switch (currentView) {
    case ONE_ON_ONE_INSTANCE_VIEWS.SUBMISSION_MESSAGE:
      return <SubmissionMessage onViewInstanceDetails={onViewInstanceDetails} />;
    case ONE_ON_ONE_INSTANCE_VIEWS.RATING:
      return (
        <SubmissionRating
          onViewInstanceDetails={onViewInstanceDetails}
          onBackButtonClick={goBack}
          isManager={isManager}
          instanceId={instanceDetails?.id}
          otherEmployee={otherEmployee}
        />
      );
    case ONE_ON_ONE_INSTANCE_VIEWS.INSTANCE_DETAILS:
    default:
      return <Questions setCurrentView={setCurrentView} reportee={reportee} />;
  }
};

const LegacyInstancePage = () => {
  const [reportee, setReportee] = useState(null);
  const [checkInInstanceId, setCheckInInstanceId] = useState(null);
  const [checkInParentId, setCheckInParentId] = useState(null);
  const [filterEmpIds, setFilterEmpIds] = useState([]);

  return (
    <OneOnOneSidePanelContextProvider>
      <LevelUpSidePanelContextProvider reportee={reportee}>
        <AgendaSidePanelContextProvider>
          <OneOnOneAnalyticsProvider>
            <LevelUpContextProvider
              employeeId={reportee?.id}
              checkInInstanceId={checkInInstanceId}
              checkInParentId={checkInParentId}
              filterEmpIds={filterEmpIds}
            >
              <OneOnOneInstanceContextProvider>
                <InstancePage
                  setReportee={setReportee}
                  setCheckInInstanceId={setCheckInInstanceId}
                  setCheckInParentId={setCheckInParentId}
                  setFilterEmpIds={setFilterEmpIds}
                />
              </OneOnOneInstanceContextProvider>
            </LevelUpContextProvider>
          </OneOnOneAnalyticsProvider>
        </AgendaSidePanelContextProvider>
      </LevelUpSidePanelContextProvider>
    </OneOnOneSidePanelContextProvider>
  );
};

export default LegacyInstancePage;
